import { requestApi, parseResponse } from '~/services/api.js';
import { requestApi as requestApiNew } from '~/services/api-new';
import { API_PAYMENT_BOOT_SERVICE_REQUESTED_LIMITS } from '~/components/account/limits/deposit/constants';

const host = '/player';
const paymentHost = '/PaymentFormDataService';
const kycHost = '/KycService';

async function getSession(playerId) {
	return parseResponse(await requestApi(`${host}/status${playerId ? `?encryptedPlayerId=${playerId}` : ''}`));
}

async function getAccount() {
	return parseResponse(await requestApi(`${host}/account`));
}

async function getPoints() {
	const response = await parseResponse(await requestApi(`${host}/points`));
	if (response.error) {
		return response;
	}
	return { points: response.amount };
}

async function getSelfExclusionValue() {
	try {
		const response = await requestApi(`${host}/bonusExclusion`);
		if (response.error) {
			return response;
		}
		return {
			playerSelfExcluded: response?.result?.blocked,
			playerBonusBlocked: response?.result?.selfExcluded === false
		};
	} catch (err) {
		throw new Error(err.message);
	}
}

async function updateResponsibleGamingFlag(playerId) {
	const response = await requestApi(`${host}/flag/setResponsibleGamingPage?encryptedPlayerId=${playerId}`);
	return response;
}

async function updateRGFlag(playerToken) {
	const endpoint = `${host}/flag/setResponsibleGamingPage`;
	const additionalHeaders = {
		Authorization: `Bearer ${playerToken}`
	};
	const response = await requestApi(endpoint, {}, 'GET', false, false, additionalHeaders);
	return response;
}

async function getBalance() {
	const response = await parseResponse(await requestApi(`${host}/balance`));
	if (response.error) {
		return response;
	}
	const { amount: balance, ...rest } = response;
	return { balance, ...rest };
}

async function getCurrency() {
	const response = await parseResponse(await requestApi(`${host}/currency`));
	if (response.error) {
		return response;
	}
	return { currency: response };
}

async function getKycValidation() {
	try {
		const response = await requestApi(`${kycHost}/public/getValidateKYCStatus/forDeposits?isMobileDevice=true`);
		return { kycValidation: response };
	} catch (err) {
		throw new Error(err.message);
	}
}

async function getPlayerTotalDeposit() {
	const response = await requestApi(`${paymentHost}/rest/player/total-deposit`);
	return { playerTotalDeposit: response };
}

async function getNumOfActiveLotteries() {
	const lotteriesResponse = await parseResponse(await requestApi('/rest/lottery/numberOfActiveLotteries'));

	return lotteriesResponse;
}

async function getNumOfActiveOffers(isLotteriesEnabled) {
	const bonusResponse = await parseResponse(await requestApi('/rest/bonus/numberofactivebonuses'));

	if (bonusResponse.error) {
		return bonusResponse;
	}

	const parsedBonuses = Number.parseInt(bonusResponse, 10);

	if (isLotteriesEnabled) {
		try {
			const lotteriesResponse = await getNumOfActiveLotteries();
			const parsedLotteries = Number.parseInt(lotteriesResponse, 10);
			const totalNumberOfActivities = parsedBonuses + parsedLotteries;

			return { numOfActiveOffers: totalNumberOfActivities };
		} catch (e) {
			console.warn(e); // eslint-disable-line
			return { numOfActiveOffers: parsedBonuses };
		}
	}

	return { numOfActiveOffers: parsedBonuses };
}

async function getRiskLevels() {
	try {
		const response = await requestApi('/mentor/riskProfile');
		if (response?.error) {
			return response;
		}
		return { riskLevels: response };
	} catch {
		return {};
	}
}

async function getSowfiInfo() {
	const response = await parseResponse(await requestApi(`${host}/sowfi/info`));
	if (response.error) {
		return response;
	}
	return { sowfiInfo: response };
}

const updatePhone = (payload, playerIdString) => requestApi(`${host}/phone`, { ...payload, playerIdString }, 'POST');

const verifyPhone = (code, playerIdString) => requestApi(`${host}/phone/verify/${code}`, { playerIdString }, 'POST');

const sendVerificationCode = playerIdString => requestApi(`${host}/phone/verify`, { playerIdString }, 'POST');

const updateEmail = payload => requestApi(`${host}/email`, payload, 'POST');

const verifyEmail = payload => requestApi(`${host}/email/verify/resend`, payload, 'POST');

const getPendingWithdrawals = () => requestApi('/PaymentFormDataService/getPendingWithdrawals?useDateFormat=true');

const cancelPendingWithdrawal = payload => requestApi(`/PaymentFormDataService/cancelWithdraw?id=${payload}`);

const updateAcceptMarketingFlag = payload =>
	requestApi('/player/updateAcceptMarketingFlag', { value: payload }, 'POST');

const updateCrossMarketingFlag = payload => requestApi('/player/updateCrossMarketingFlag', { value: payload }, 'POST');

const getRemainingBonusValue = async () => parseResponse(await requestApi('/rest/bonus/playerRemainingBonusValue'));

const getPlayerAtLeastOneFinalizedInterviewExists = async () => {
	try {
		const { result } = await requestApiNew({
			endpoint: `${API_PAYMENT_BOOT_SERVICE_REQUESTED_LIMITS}/playerRequestedDepositLimit/hasPlayerAtLeastOneFinalizedInterview`
		});
		return result;
	} catch (error) {
		// eslint-disable-next-line no-console
		console.error('hasPlayerAtLeastOneFinalizedInterview => error:', error);
		return false;
	}
};

async function updateLimits(playerToken, payload) {
	const endpoint = `${host}/limit/list`;
	const additionalHeaders = {
		Authorization: `Bearer ${playerToken}`
	};
	const response = await requestApi(endpoint, payload, 'POST', false, false, additionalHeaders);
	return response;
}

export {
	getSession,
	getAccount,
	getBalance,
	getPoints,
	getCurrency,
	getNumOfActiveOffers,
	updatePhone,
	verifyPhone,
	updateEmail,
	sendVerificationCode,
	verifyEmail,
	getPendingWithdrawals,
	cancelPendingWithdrawal,
	getKycValidation,
	getPlayerTotalDeposit,
	getSowfiInfo,
	getRiskLevels,
	updateResponsibleGamingFlag,
	updateAcceptMarketingFlag,
	updateCrossMarketingFlag,
	getRemainingBonusValue,
	getSelfExclusionValue,
	getPlayerAtLeastOneFinalizedInterviewExists,
	updateRGFlag,
	updateLimits
};
